import { ACCOUNT__PERMISSIONS } from '@/backend/modules/account/permissions'
import { CAREER__PERMISSIONS } from '@/backend/modules/career/permissions'
import { COURSE__PERMISSIONS } from '@/backend/modules/course/permissions'
import { DOCS__PERMISSIONS } from '@/backend/modules/docs/permissions'
import { MARKETPLACE__PERMISSIONS } from '@/backend/modules/marketplace/permissions'
import { NEWS__PERMISSIONS } from '@/backend/modules/news/permissions'
import { PHONE_CALL_BOOKING__PERMISSIONS } from '@/backend/modules/phone-call-booking/permissions'
import { PROBLEM_BASE__PERMISSIONS } from '@/backend/modules/problem/permissions'
import { TAG__PERMISSIONS } from '@/backend/modules/tag/permissions'

export const TAG__TAG_TYPE_STRUCTERS = {
  CATEGORY: 'category',
  SKILL: 'skill'
} as const

export type TagTypeStructers =
  (typeof TAG__TAG_TYPE_STRUCTERS)[keyof typeof TAG__TAG_TYPE_STRUCTERS]

export const TAG_TYPE_STRUCTERS_DESCRIPTIONS: Record<TagTypeStructers, string> = {
  [TAG__TAG_TYPE_STRUCTERS.CATEGORY]: 'Kategoria, która jedynie grupuje poszczególne umiejętności',
  [TAG__TAG_TYPE_STRUCTERS.SKILL]: 'Umiejętność'
} as const

export const TAG__TYPE = {
  ...TAG__TAG_TYPE_STRUCTERS
}
export type TagType = (typeof TAG__TYPE)[keyof typeof TAG__TYPE]

export const TAG_TYPE_DESCRIPTIONS: Record<TagType, string> = {
  ...TAG_TYPE_STRUCTERS_DESCRIPTIONS
} as const

export const PROBLEM__TYPE = {
  QUIZ: 'quiz',
  BASIC: 'basic'
} as const

export type ProblemType = (typeof PROBLEM__TYPE)[keyof typeof PROBLEM__TYPE]

export const PROBLEM_TYPE_DESCRIPTIONS: Record<ProblemType, string> = {
  [PROBLEM__TYPE.QUIZ]: 'Test jednokrotnego lub wielokrotnego wyboru',
  [PROBLEM__TYPE.BASIC]: 'Zadanie otwarte wymagające napisania rozwiązania'
} as const

export const COURSE__ENROLLMENT_METHOD = {
  PAYMENT: 'payment',
  SUBSCRIPTION: 'subscription'
} as const

export type CourseEnrollmentMethod =
  (typeof COURSE__ENROLLMENT_METHOD)[keyof typeof COURSE__ENROLLMENT_METHOD]

export const COURSE__ENROLLMENT_METHOD_DESCRIPTIONS: Record<CourseEnrollmentMethod, string> = {
  [COURSE__ENROLLMENT_METHOD.PAYMENT]: 'Jednorazowa płatność za dostęp do kursu',
  [COURSE__ENROLLMENT_METHOD.SUBSCRIPTION]: 'Dostęp w ramach subskrypcji'
} as const

export const MARKETPLACE__COURSE_AUDIENCE = {
  PRIMARY_SCHOOL: 'primary_school',
  HIGH_SCHOOL: 'high_school',
  UNIVERSITY: 'university',
  PROFESSIONAL: 'professional'
} as const

export type MarketplaceCourseAudience =
  (typeof MARKETPLACE__COURSE_AUDIENCE)[keyof typeof MARKETPLACE__COURSE_AUDIENCE]

export const MARKETPLACE__COURSE_AUDIENCE_DESCRIPTIONS: Record<MarketplaceCourseAudience, string> =
  {
    [MARKETPLACE__COURSE_AUDIENCE.PRIMARY_SCHOOL]: 'Uczniowie szkół podstawowych',
    [MARKETPLACE__COURSE_AUDIENCE.HIGH_SCHOOL]: 'Uczniowie szkół średnich',
    [MARKETPLACE__COURSE_AUDIENCE.UNIVERSITY]: 'Studenci',
    [MARKETPLACE__COURSE_AUDIENCE.PROFESSIONAL]: 'Osoby pracujące'
  } as const

export const MARKETPLACE__COURSE_DELIVERY_METHOD = {
  ONLINE: 'online',
  OFFLINE: 'offline'
} as const

export type MarketplaceCourseDeliveryMethod =
  (typeof MARKETPLACE__COURSE_DELIVERY_METHOD)[keyof typeof MARKETPLACE__COURSE_DELIVERY_METHOD]

export const MARKETPLACE_COURSE_DELIVERY_METHOD_DESCRIPTIONS: Record<
  MarketplaceCourseDeliveryMethod,
  string
> = {
  [MARKETPLACE__COURSE_DELIVERY_METHOD.ONLINE]: 'Kurs prowadzony zdalnie',
  [MARKETPLACE__COURSE_DELIVERY_METHOD.OFFLINE]: 'Kurs prowadzony stacjonarnie'
} as const

export const TAG__QUALITY = {
  VERY_LOW: 'very_low',
  LOW: 'low',
  BELOW_AVERAGE: 'below_average',
  AVERAGE: 'average',
  ABOVE_AVERAGE: 'above_average',
  MODERATE: 'moderate',
  GOOD: 'good',
  VERY_GOOD: 'very_good',
  EXCELLENT: 'excellent',
  OUTSTANDING: 'outstanding'
} as const

export type TagQuality = (typeof TAG__QUALITY)[keyof typeof TAG__QUALITY]

export const TAG_QUALITY_DESCRIPTIONS: Record<TagQuality, string> = {
  [TAG__QUALITY.VERY_LOW]: 'Bardzo niska',
  [TAG__QUALITY.LOW]: 'Niska',
  [TAG__QUALITY.BELOW_AVERAGE]: 'Poniżej średniej',
  [TAG__QUALITY.AVERAGE]: 'Średnia',
  [TAG__QUALITY.ABOVE_AVERAGE]: 'Powyżej średniej',
  [TAG__QUALITY.MODERATE]: 'Umiarkowana',
  [TAG__QUALITY.GOOD]: 'Dobra',
  [TAG__QUALITY.VERY_GOOD]: 'Bardzo dobra',
  [TAG__QUALITY.EXCELLENT]: 'Doskonała',
  [TAG__QUALITY.OUTSTANDING]: 'Wybitna'
} as const

export const TAG__GENERAL_SKILL_LEVEL = {
  BASIC: 'basic',
  PROFICIENT: 'proficient',
  ADVANCED: 'advanced',
  EXPERT: 'expert'
} as const
export type TagGeneralSkillLevel =
  (typeof TAG__GENERAL_SKILL_LEVEL)[keyof typeof TAG__GENERAL_SKILL_LEVEL]

export const TAG__GENERAL_SKILL_LEVEL_VALUES = [
  TAG__GENERAL_SKILL_LEVEL.BASIC,
  TAG__GENERAL_SKILL_LEVEL.PROFICIENT,
  TAG__GENERAL_SKILL_LEVEL.ADVANCED,
  TAG__GENERAL_SKILL_LEVEL.EXPERT
] as const

export const TAG_GENERAL_SKILL_LEVEL_DESCRIPTIONS: Record<TagGeneralSkillLevel, string> = {
  [TAG__GENERAL_SKILL_LEVEL.BASIC]:
    'Podstawowa znajomość tematu; wymaga regularnej pomocy lub nadzoru.',
  [TAG__GENERAL_SKILL_LEVEL.PROFICIENT]:
    'Samodzielnie stosuje wiedzę i umiejętności w typowych sytuacjach; okazjonalnie wymaga wsparcia.',
  [TAG__GENERAL_SKILL_LEVEL.ADVANCED]:
    'Zdolność samodzielnego radzenia sobie w trudnych sytuacjach; pełni rolę doradczą dla innych mentorów.',
  [TAG__GENERAL_SKILL_LEVEL.EXPERT]:
    'Wybitna wiedza i umiejętności; kreuje nowe rozwiązania i strategie na poziomie firmy lub branży.'
} as const

export const DOCS__READING_STATUS = {
  NOT_READ: 'not_read',
  READ: 'read',
  IN_PROGRESS: 'in_progress',
  WANT_TO_READ: 'want_to_read'
} as const

export type DocsReadingStatus = (typeof DOCS__READING_STATUS)[keyof typeof DOCS__READING_STATUS]

export const DOCS_READING_STATUS_DESCRIPTIONS: Record<DocsReadingStatus, string> = {
  [DOCS__READING_STATUS.NOT_READ]: 'Nie przeczytane',
  [DOCS__READING_STATUS.READ]: 'Przeczytane',
  [DOCS__READING_STATUS.IN_PROGRESS]: 'W trakcie czytania',
  [DOCS__READING_STATUS.WANT_TO_READ]: 'Chcę przeczytać'
} as const

export const MENTOR_REPLY_TIME = {
  STANDARD: '48 godzin',
  MEDIUM: '24 godziny',
  PRIORITY: 'Priorytet odpowiedzi'
} as const

export type MentorReplyTime = (typeof MENTOR_REPLY_TIME)[keyof typeof MENTOR_REPLY_TIME]

export const SUBSCRIPTION = {
  PER_4_WEEKS: '4 tygodnie',
  PER_12_WEEKS: '12 tygodni',
  YEARLY: 'Rok'
} as const

export type Subscription = (typeof SUBSCRIPTION)[keyof typeof SUBSCRIPTION]

export const PERMISSIONS = {
  ...COURSE__PERMISSIONS,
  ...MARKETPLACE__PERMISSIONS,
  ...ACCOUNT__PERMISSIONS,
  ...PROBLEM_BASE__PERMISSIONS,
  ...TAG__PERMISSIONS,

  ...CAREER__PERMISSIONS,
  ...NEWS__PERMISSIONS,
  ...PHONE_CALL_BOOKING__PERMISSIONS,
  ...DOCS__PERMISSIONS
}
export type Permission = {
  dbName: string
  name: string
  description: string
}
